// ArgbSliderPanel — 旧 Java applet `ColorMakeCanvas.java` (RGB / HSB 両モード) の
// 50x74 px Canvas 直描画版.
//
// Phase 6.7-B-fix-2B: 従来の HTML `<input type="range">` 4 本 + CSS gradient
// 実装は `writing-mode: vertical-lr` を使っても thumb 周辺の余白で横幅が
// 広くなり, 左サイドバー 140px が窮屈だった (元々の thumb サイズの最低保証
// が現代ブラウザでは ~16px で, 4 本並べると 80px 以上必要)。旧版同様に
// 50px 幅 Canvas に直描画することで「左サイドバー 140px 内に余裕で収まる」
// 設計に戻す.
//
// 描画ロジックは `drawArgbCanvas.ts` の pure function に切り出し済 (node 環境で
// fillRect / fillText を spy するテスト可能性を確保).
//
// ## ARGB ⇄ HSB toggle (2026-05-31, alpha-feedback §4)
//
// 経緯: Phase 6.7 サブ task (consult 2026-05-07) で旧 `ColorMakeCanvas.java:467-490`
// の Meta+click トグルを「右クリック (oncontextmenu) + Alt+クリック」の 2 経路で
// 移植していたが、α 期に **ペンタブ / iPad で長押し = 右クリック相当が意図せず
// 発火し HSV に切り替わる** 課題を作者本人が観測 (alpha-feedback §4). 旧 Java 版は
// 「OS 設定で対処」と案内していたが、新版では UI レベルで根本対応する方針が確定.
//
// 確定実装:
//   - Canvas 下部に **MASK ⇔ 逆マスク toggle button と同じパターン**の明示的
//     toggle button を配置 (= 「ARGB」「HSB」表示, aria-pressed で現在モード).
//   - **Alt+クリック / 右クリック (contextmenu) での toggle 経路は撤去**.
//   - 切替はボタンクリックのみ (= 誤動作の余地を消す).
//   - 内部 pen.color (rgb24) は不変、**表示時のみ HSV に変換**、**編集時のみ
//     HSV → rgb24 で逆変換** (= colorWheel.ts の `hsb255FromRgb24` /
//     `rgb24FromHsb255` を使用) — この方針は維持.
//   - mode 切替時に dragChannel ref をクリア (= 操作中ドラッグが切れる、整合確保).
//   - Alpha バーは両モード共通 (= alpha 値は色空間と独立、旧版踏襲).
//
// ## ARGB エリア統合 (2026-05-31, layer-popup-visibility-toggle)
//
// 経緯: α 期 (`alpha-feedback-2026-05-31.md §2-A`) で「レイヤー窓を消したい / 旧式
// (= 1 layer / panel なし) の手触りが欲しい」声を受けて、LayerPopup の可視 toggle を
// ArgbSliderPanel 内に統合する設計が確定 (`docs/_wip/w3-classic-layers.md §2.2.1`).
// 同時に旧独立 `[ARGB] / [HSB]` toggle button (= alpha-feedback §4) も canvas 内
// channel labels と統合して 1 つの label-as-button (`[A R G B]` / `[A H S V]`) に
// 寄せ、ARGB エリア縦寸法を変えずに Layer toggle を増設できるようにする.
//
// ## post-implementation 調整 (2026-05-31, `w3-classic-layers.md §2.2.2`)
//
// commit 34262ad で §2.2.1 を実装後、実機 UI 確認で 2 点を調整:
//   1. `[A R G B]` / `[A H S V]` toggle の bg を **MaskPanel と同型** (= ARGB
//      mode → `bg-gray-400 text-white`、HSB mode → `bg-black text-white`) に
//      揃え直し. 採用時は `legacyToggleButtonClass(false)` (= idle 白) に倒したが、
//      **旧独立 ARGB ⇄ HSB toggle は `bg-gray-400` / `bg-black` で mode を bg 色で
//      表現していた歴史**と MaskPanel `normal/inverse` (= 灰/黒) の流儀に揃える方が
//      mode 状態が一目で判る (= MaskPanel.tsx:100-101 と同 pattern).
//   2. `[レイヤー]` toggle を **ToolPanel の Preview button 直下に移動**
//      (= visibility toggle 群が散らかる懸念の解消、ToolPanel.tsx に集約). ここから
//      `layerPopupVisible` / `onToggleLayerPopup` 関連 props と JSX は撤去.
//      `isDragging` 経路は ARGB toggle の drag-up 誤反応防止のためにそのまま残す
//      (= Layer toggle は外れたが ARGB toggle は canvas drag 範囲に隣接して残るため).
//
// 最終構造 (`outsetFrame` fieldset 化):
//   ┌─────────────┐  ← outsetFrame border (MaskPanel と同型)
//   │ ARGB canvas │    50 × 64 (= 旧 74 から channel labels 領域 10px を撤去)
//   │  (50 × 64)  │
//   ├─────────────┤
//   │ [A R G B]   │  ← ARGB ⇄ HSB toggle (bg-gray-400 / bg-black、h-5)
//   └─────────────┘
//
// drag-up 誤反応防止 (重要、§2.2.1):
//   ARGB バー drag end (pointerup) が `[A R G B]` toggle 上に来ても誤発火させない.
//   3 段の対策:
//     1. canvas 側 `setPointerCapture` で drag 中の pointer event を canvas に集約
//        (= 既存 R10 fix の経路をそのまま流用).
//     2. toggle button は `onClick` のみ使用 (= `onPointerDown` / `onPointerUp` を
//        付けない、ブラウザ標準で target 不一致時は click 発火せず).
//     3. drag 中 (= `isDragging` state = canvas pointerdown 中) は ARGB toggle
//        button に `pointer-events: none` を CSS で当てる (= hover オレンジ点灯も
//        同時に抑止される、視覚的にも誤反応していないことを示す).
//
// Phase 6.7 R10 (consult 2026-05-05): ARGB バーの「画面端で値が止まる」バグ修正.
//   - 症状: A/R/G/B どのバーでもドラッグで 0xFF / 0x00 に到達できない.
//   - 真因 1: `pointermove` 内の早期 return `if (yCanvas < 0 || yCanvas > 63) return;`
//     が y 範囲外で値更新を弾いていた. `yToValue` は内部で 0..63 clamp 済なので
//     その前に return していたのが本質. → 撤廃して `yToValue` の clamp に任せる.
//   - 真因 2: `xToChannel` を `pointermove` 毎に再評価していたため, ドラッグ中に
//     x が canvas 外に出ると null 返却 → 早期 return → 値が止まる. → channel を
//     `pointerdown` 時に確定して useRef に保持し, `pointermove` では再評価しない
//     (旧版 `dragColor` と同じ思想 — `ColorMakeCanvas.java:467-490` 踏襲).
//   - 真因 3 (構造的): channel を解放しないとずっと lock が残る. `pointerup` /
//     `lostpointercapture` で `dragChannel.current = null` に戻す.
//   - 「画面端で値が止まる」問題が構造的に発生しない 3 点セット (R11 の太さ調整
//     バーや、今後新規の Canvas slider もこのパターンで実装する).
//
// 申し送り (Phase 6.7-B-fix-2C 以降):
// - ここで確立した「pure draw function + ref-based React canvas + pointer
//   events」の 3 点セットは, ペン種アイコン化 / プレビュー窓 / マスク色
//   ピッカー等の旧版 GIF を Canvas 化する際に流用できる.
// - A バー背景は旧版完全踏襲 (白塗りで A=255 / 白背景 = 識別不可) ではなく
//   薄 checker 模様を敷く方針を採用. 旧版にはなかった視認性向上対応.
//
// (architecture.md §16 / 旧 ColorMakeCanvas.java line 100-329 / alpha-feedback §4 /
//  docs/_wip/w3-classic-layers.md §2.2.1)

import { useEffect, useRef, useState } from 'react';

import type { PenConfig } from '../../../domain/PenConfig.js';
import { hsb255FromRgb24, rgb24FromHsb255 } from '../../color/colorWheel.js';
import {
  ARGB_CANVAS_HEIGHT,
  ARGB_CANVAS_WIDTH,
  type ArgbChannel,
  type ArgbMode,
  drawArgbCanvas,
  xToChannel,
  yToValue,
} from './drawArgbCanvas.js';
import { insetFrame, outsetFrame } from './legacyFrames.js';
import { rgbFromChannels } from './PalettePanel.js';

/** RGB24 → 各 8bit チャネル抽出. */
const rChan = (rgb: number): number => (rgb >>> 16) & 0xff;
const gChan = (rgb: number): number => (rgb >>> 8) & 0xff;
const bChan = (rgb: number): number => rgb & 0xff;

export interface ArgbSliderPanelProps {
  readonly pen: PenConfig;
  readonly onColorChange: (color: number) => void;
  readonly onAlphaChange: (alpha: number) => void;
  /**
   * 表示モード. 'argb' (= R/G/B + alpha) または 'hsb' (= H/S/V + alpha).
   * 省略時は 'argb' (後方互換).
   */
  readonly mode?: ArgbMode;
  /**
   * モード toggle 要求. label-as-button (`[A R G B]` / `[A H S V]`) から呼ばれる.
   * caller (= ClassicLayout) が受けて state を flip する責務. 省略時は toggle 機能
   * 無効 (= MVP の最初期や他 layout で toggle 不要なケースの後方互換).
   *
   * 2026-05-31 alpha-feedback §4: 旧 Alt+クリック / 右クリック経路は撤去済.
   * 切替はこの button からの onClick 経由のみ.
   *
   * 2026-05-31 layer-popup-visibility-toggle (`w3-classic-layers.md §2.2.1`):
   * 旧独立 `[ARGB] / [HSB]` toggle button を canvas 内 channel labels と統合した
   * label-as-button (`[A R G B]` / `[A H S V]`) に置換. prop の意味は不変.
   */
  readonly onModeToggle?: () => void;
}

export const ArgbSliderPanel = ({
  pen,
  onColorChange,
  onAlphaChange,
  mode = 'argb',
  onModeToggle,
}: ArgbSliderPanelProps): React.ReactNode => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  /**
   * R10: ドラッグ中の channel を `pointerdown` 時に確定して保持する.
   * `pointermove` 中は同じ channel を更新し続け, x が canvas 外に出ても
   * 隣 channel に流れない. `pointerup` / `lostpointercapture` で null に戻す.
   * 旧版 `ColorMakeCanvas.dragColor` (line 467-490) と同じ思想.
   *
   * HSB モードでも `ArgbChannel` 型 ('a'/'r'/'g'/'b') のままでバー位置を表す.
   * R 列が H, G 列が S, B 列が V に役割が変わるだけで, x 位置は完全同一なので
   * channel ID も流用する (= UI モデル簡潔化、旧 Java も valueR/valueG/valueB を
   * HSV にも使い回していた).
   */
  const dragChannel = useRef<ArgbChannel | null>(null);

  /**
   * canvas pointerdown 中フラグ. 2026-05-31 layer-popup-visibility-toggle §2.2.1
   * drag-up 誤反応防止対策の **3 段目**: drag 中は ARGB toggle button (`[A R G B]` /
   * `[A H S V]`) を `pointer-events: none` にして、pointerup が toggle button の
   * 上に来ても click が発火しないようにする (= hover オレンジ点灯も同時に抑止).
   *
   * 2026-05-31 §2.2.2 post-implementation 調整で `[レイヤー]` toggle を ToolPanel
   * へ移動したが、ARGB toggle は引き続き canvas drag 範囲に隣接して残るため、
   * この経路は維持する.
   *
   * - `handleDown` で true → `handleUp` / `onLostPointerCapture` で false.
   * - `dragChannel.current` とは別 ref と React state の二重管理になるが、CSS
   *   `pointer-events` を切り替えるために再 render を trigger する必要がある
   *   → React state にする (= useRef では DOM 更新が起きない).
   * - 1 段目 `setPointerCapture` (= pointer event を canvas に集約) と 2 段目
   *   「toggle button は onClick のみ」(= target 不一致で click 不発火) と
   *   組み合わせて drag-up 誤反応を構造的に消す.
   */
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // 表示用 channel value. ARGB なら pen.color から R/G/B を取り出す.
  // HSB なら pen.color から HSV (0..255 normalized) に変換する.
  // pen.color (rgb24) を single source of truth に保つ (= mode 切替で値が変わらない).
  // primitive 値で保持することで useEffect / 計算系の deps を ESLint/Biome に説明しやすくする.
  const valueA = pen.alpha;
  const valueR = rChan(pen.color);
  const valueG = gChan(pen.color);
  const valueB = bChan(pen.color);
  const { h: valueH, s: valueS, v: valueV } = hsb255FromRgb24(pen.color);

  // 描画は値が変わるたびに 1 回. ctx は毎回 getContext で取り直しても
  // ブラウザ側で同一インスタンスを返す (cheap).
  // mode 変更時にも再描画するよう deps に mode を含める.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (mode === 'argb') {
      drawArgbCanvas(ctx, { valueA, valueR, valueG, valueB }, 'argb');
    } else {
      drawArgbCanvas(ctx, { valueA, valueH, valueS, valueV }, 'hsb');
    }
  }, [mode, valueA, valueR, valueG, valueB, valueH, valueS, valueV]);

  /**
   * 与えられた canvas 座標 (yCanvas) と channel から、対応する `onColorChange` /
   * `onAlphaChange` を発火する.
   *
   * - ARGB モード: channel = 'a'/'r'/'g'/'b'. 既存 R10 fix と同じ流儀.
   * - HSB モード: channel は同じ ID ('a'/'r'/'g'/'b') だが意味は H/S/V に rename
   *   される (= 旧 Java の valueR/G/B を HSV 値置換と同じ). HSV 値を 1 つ更新したら
   *   現在の他 2 値と組み合わせて rgb24 に逆変換し `onColorChange` に流す.
   *
   * `yToValue` は内部で 0..63 clamp 済なので y 範囲外でもそのまま渡してよい.
   */
  const applyValue = (ch: ArgbChannel, yCanvas: number): void => {
    const value = yToValue(yCanvas);
    if (ch === 'a') {
      onAlphaChange(value);
      return;
    }
    if (mode === 'argb') {
      if (ch === 'r') onColorChange(rgbFromChannels(value, valueG, valueB));
      else if (ch === 'g') onColorChange(rgbFromChannels(valueR, value, valueB));
      else if (ch === 'b') onColorChange(rgbFromChannels(valueR, valueG, value));
      return;
    }
    // HSB mode: R 列 = H, G 列 = S, B 列 = V.
    if (ch === 'r') onColorChange(rgb24FromHsb255(value, valueS, valueV));
    else if (ch === 'g') onColorChange(rgb24FromHsb255(valueH, value, valueV));
    else if (ch === 'b') onColorChange(rgb24FromHsb255(valueH, valueS, value));
  };

  /**
   * pointerdown — channel をここで確定して `dragChannel` に保持する.
   * 以降 `pointermove` は `dragChannel.current` を再利用し, `xToChannel` を
   * 毎 event で再評価しない (画面外に出ても隣 channel に流れない).
   *
   * 2026-05-31 alpha-feedback §4: 旧 Alt+クリック / 右クリック経路で発動して
   * いた ARGB ⇄ HSB toggle はここから撤去. ペンタブ / iPad で長押し = 右クリック
   * 相当が意図せず発火して HSV に切り替わる課題を根本対応するため. 切替は
   * label-as-button (`[A R G B]` / `[A H S V]` toggle) の onClick からのみ.
   *
   * ドラッグ中に canvas 外へ出ても tracking を維持するため `setPointerCapture`.
   * これで pointermove が canvas 内座標基準で取れる. 2026-05-31
   * layer-popup-visibility-toggle §2.2.1 の drag-up 誤反応防止 1 段目でも
   * これが必須 (= 同時に 3 段目で isDragging=true を立てて ARGB toggle button を
   * pointer-events: none にする).
   */
  const handleDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    // 右クリック (button=2、ペンタブの barrel button 含む) はバー操作の対象外.
    // 2026-05-31 alpha-feedback §4 follow-up: F の handler 撤去後、右クリックが
    // 通常クリック扱いで値を変えてしまっていた問題への補修.
    if (e.button === 2) return;
    e.currentTarget.setPointerCapture(e.pointerId);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const xCanvas = ((e.clientX - rect.left) / rect.width) * ARGB_CANVAS_WIDTH;
    const yCanvas = ((e.clientY - rect.top) / rect.height) * ARGB_CANVAS_HEIGHT;

    // pointerdown 時は y 範囲外のクリックを無視する.
    // 2026-05-31 layer-popup-visibility-toggle: ARGB_CANVAS_HEIGHT が 74 → 64 に縮んだ
    // (= 下部 10px の channel labels 領域を撤去) ので、有効な y 範囲は 0..63
    // (= ARGB_BAR_AREA_HEIGHT - 1 まで).
    if (yCanvas < 0 || yCanvas > ARGB_CANVAS_HEIGHT - 1) return;

    const ch = xToChannel(xCanvas);
    if (!ch) return;
    dragChannel.current = ch;
    // drag-up 誤反応防止 §2.2.1 3 段目: drag 中 ARGB toggle button を非アクティブ化.
    setIsDragging(true);
    applyValue(ch, yCanvas);
  };

  /**
   * pointermove — `dragChannel.current` が non-null のときのみ値更新.
   * x の再評価はしない (channel は down 時に確定). y は `yToValue` の
   * 内部 clamp に任せて, canvas 外に出ても 0xFF / 0x00 に到達できる.
   */
  const handleMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    // 押下中のみ反応. dragChannel が null なら down 時にラベル領域から
    // 始まった等で lock していない → 何もしない.
    if (e.buttons === 0) return;
    if (dragChannel.current === null) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const yCanvas = ((e.clientY - rect.top) / rect.height) * ARGB_CANVAS_HEIGHT;
    applyValue(dragChannel.current, yCanvas);
  };

  /**
   * pointerup / lostpointercapture — channel lock を解放 + isDragging を倒す.
   * isDragging false 化で ARGB toggle button の `pointer-events: none` も解け、
   * 直後の click 操作は通常通り受け付けるようになる (= drag-up 誤反応防止 §2.2.1).
   */
  const handleUp = (): void => {
    dragChannel.current = null;
    setIsDragging(false);
  };

  /**
   * mode 切替時に dragChannel をクリアする (整合確保).
   * mode 変更は user の明示的な action 経由で起こるが、保険として ref も同期する.
   * biome-ignore lint/correctness/useExhaustiveDependencies: ref mutation は re-render しないが
   *   `mode` 変化時に dragChannel をリセットする副作用が必要 (= 操作中ドラッグの解除、R10 fix の延長).
   */
  useEffect(() => {
    dragChannel.current = null;
  }, [mode]);

  // aria-label は mode によって表記を変える (= スクリーンリーダで現在モードが伝わる).
  const ariaLabel =
    mode === 'argb'
      ? `ARGB スライダ A=${valueA} R=${valueR} G=${valueG} B=${valueB}`
      : `HSB スライダ A=${valueA} H=${valueH} S=${valueS} V=${valueV}`;

  // 2026-05-31 layer-popup-visibility-toggle (`w3-classic-layers.md §2.2.1`):
  // MaskPanel と同型の outsetFrame fieldset 化. 内側に canvas + ARGB toggle の
  // 2 要素をぴったり接続 (= 隙間なし). MaskPanel.tsx:67-78 と完全同型 (`w-[50px]` で
  // ARGB canvas 50px と幅統一).
  //
  // 2026-05-31 §2.2.2 post-implementation 調整: `[レイヤー]` toggle は ToolPanel
  // へ移動. ここでは canvas + ARGB ⇄ HSB toggle の 2 要素のみ.
  //
  // drag-up 誤反応防止 §2.2.1 3 段目: drag 中は ARGB toggle button に
  // `pointer-events: none` を当てる (= hover オレンジ点灯も同時に抑止される).
  const buttonPointerEventsStyle: React.CSSProperties = {
    pointerEvents: isDragging ? 'none' : 'auto',
  };
  return (
    <fieldset
      aria-label="色"
      data-manual="color.sliders"
      // MaskPanel.tsx:67-78 と同型: outsetFrame + p-0 + bg-white + w-[50px].
      // self-center で左サイドバー内中央寄せ (= MaskPanel と同流儀).
      className={`flex w-[50px] flex-col self-center bg-white p-0 ${outsetFrame}`}
    >
      {/* legend は a11y 専用に sr-only 化 (MaskPanel と同流儀). 視覚的 label は
          下の label-as-button (`[A R G B]` / `[A H S V]`) が担う. */}
      <legend className="sr-only">色</legend>

      <canvas
        ref={canvasRef}
        width={ARGB_CANVAS_WIDTH}
        height={ARGB_CANVAS_HEIGHT}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        onLostPointerCapture={handleUp}
        // 右クリック (= ペンタブの barrel button 含む) で browser context menu を
        // 出さない. 上の handleDown の `e.button === 2` early-return と組み合わせて
        // 「右クリックは完全に無反応」を実現する.
        onContextMenu={(e) => e.preventDefault()}
        // pixelated: 50x64 を CSS で多少拡大しても旧版風の鋭い見た目を維持.
        // touch-action none: ドラッグ中の pull-to-refresh / scroll を抑止.
        style={{
          imageRendering: 'pixelated',
          cursor: 'crosshair',
          touchAction: 'none',
          width: `${ARGB_CANVAS_WIDTH}px`,
          height: `${ARGB_CANVAS_HEIGHT}px`,
        }}
        // canvas は role=img + aria-label の組合せが現実的 (4 channel をまとめた
        // slider は role=slider に乗らないため). 値変化はビジュアルとペン色
        // プレビューで読み取られる前提.
        role="img"
        aria-label={ariaLabel}
      />

      {/* ARGB ⇄ HSB label-as-button toggle. caller が onModeToggle を渡したとき
          のみ描画する (= 後方互換).
          2026-05-31 layer-popup-visibility-toggle (`w3-classic-layers.md §2.2.1`):
          旧 canvas 内 channel labels ('A/R/G/B' / 'A/H/S/V') + 旧独立 bg-black /
          bg-gray-400 toggle button を 1 つの label-as-button に統合.
          2026-05-31 §2.2.2 post-implementation 調整 (`w3-classic-layers.md §2.2.2`):
          採用時は `legacyToggleButtonClass(false)` (= idle 白) に倒したが、**旧独立
          ARGB ⇄ HSB toggle が `bg-gray-400` / `bg-black` で mode を bg 色で表現
          していた歴史**と MaskPanel `normal/inverse` 流儀に揃え直す:
            - ARGB mode → `bg-gray-400 text-white` (= MaskPanel `normal` 同型)
            - HSB mode → `bg-black text-white` (= MaskPanel `inverse` 同型)
          色で mode 状態が一目で判る. 文字 `[A R G B]` / `[A H S V]` (4 文字 / 旧
          channel labels と等幅 spacing で視覚的に同等) と click で argb ⇄ hsb の
          挙動は不変. */}
      {onModeToggle ? (
        <button
          type="button"
          aria-label="ARGB / HSB 切替"
          aria-pressed={mode === 'hsb'}
          onClick={onModeToggle}
          // drag-up 誤反応防止 §2.2.1 3 段目: drag 中は pointer-events: none で
          // click 自体が来ない (= hover オレンジ点灯も抑止される、視覚的にも
          // 誤反応していないことを示す). isDragging false に戻れば通常動作.
          style={buttonPointerEventsStyle}
          // MaskPanel.tsx:98-102 と同 pattern: bg-gray-400 (normal) / bg-black
          // (inverse) で mode を表現. `[A R G B]` を等幅 spacing で表示するため
          // tracking-widest を当てる (= 旧 channel labels の 4/16/28/40 の x 配置に
          // 視覚的に対応). focus ring 抑制は旧 Java applet UI 完全再現要求
          // (MaskPanel と同条件).
          className={`relative flex h-5 w-[50px] items-center justify-center whitespace-nowrap text-[10px] font-bold tracking-widest focus:outline-none focus-visible:outline-none ${insetFrame} ${
            mode === 'hsb'
              ? 'bg-black text-white hover:bg-[var(--color-accent)]'
              : 'bg-gray-400 text-white hover:bg-[var(--color-accent)]'
          }`}
          title={mode === 'argb' ? 'クリックで HSB へ切替' : 'クリックで ARGB へ切替'}
        >
          {mode === 'hsb' ? 'AHSV' : 'ARGB'}
        </button>
      ) : null}
    </fieldset>
  );
};
